import React, { LegacyRef, useEffect, useMemo, useState } from 'react';
import { refreshAdv } from '@sky-uk/sky-adv';
import { LiveblogTenant } from '../../typings/liveblog-themes';
import { isClient, isTG24WebView } from '../../utils/device-utils';
import { track } from '../../utils/tracking-data';
import t from '../../labels/labelling-utils';
import { deviceUtils } from '../../utils';
import { usePostObserverOnBottom } from '../../hooks/use-post-observer-on-bottom';
import { IClassNames } from '../../utils/styles';
import Adv from '../adv/adv';

const ADV_TOP_DATA_NODE_SELECTOR = '#dataAdvLiveblog';

interface TimelineSectionProps {
  postId: string;
  children: JSX.Element;
  tenant?: LiveblogTenant;
  cn: IClassNames;
  index: number;
  newPostsCounter: number;
  isSlimTimeline: boolean;
  isObserved?: boolean;
  isWebView?: boolean;
  isHidden?: boolean;
  handleInfiniteScroll?: Function;
  pageNumber: number;
}

const TimelineSection = ({
  postId,
  tenant,
  children,
  cn,
  index,
  newPostsCounter,
  isSlimTimeline,
  isObserved,
  isWebView,
  isHidden,
  handleInfiniteScroll,
  pageNumber,
}: TimelineSectionProps) => {
  const [showAdv, setShowAdv] = useState<boolean>(false);

  // HANDLERS
  const onFirstIntersect = () => {
    handleInfiniteScroll?.();
    setShowAdv(true);
  };
  const onEnterInView = () => {
    if (!isWebView || isTG24WebView(isWebView, tenant)) {
      track('change_gallery', { hash: `${pageNumber}` });
    }
  };
  const onLeaveView = () => {
    if (!isWebView || isTG24WebView(isWebView, tenant)) {
      track('change_gallery', { hash: `${pageNumber - 1}` });
    }
  };

  const [ref] = usePostObserverOnBottom({
    isObserved,
    onFirstIntersect,
    onEnterInView,
    onLeaveView,
  });
  const $advTopDataNode = useMemo(
    () =>
      isClient()
        ? document?.querySelector(ADV_TOP_DATA_NODE_SELECTOR)
        : undefined,
    [],
  );
  const shouldInjectAdvOnMobile =
    isClient() &&
    isObserved &&
    !isWebView &&
    !isHidden &&
    deviceUtils.isMobile();
  const shouldRefreshAdvOnDesktop =
    isClient() &&
    isObserved &&
    !isWebView &&
    !isHidden &&
    !deviceUtils.isMobile();

  // ON DESKTOP we refresh the mpu top on sidebar
  useEffect(() => {
    if (shouldRefreshAdvOnDesktop && showAdv) {
      refreshAdv($advTopDataNode as HTMLElement, false);
    }
  }, [$advTopDataNode, shouldRefreshAdvOnDesktop, showAdv]);

  return (
    <section
      role="listitem"
      ref={ref as LegacyRef<HTMLElement> | undefined}
      className={`${cn.TimelineSection} ${
        isHidden ? cn.TimelineSectionHidden : ''
      }`}
      key={postId}
      id={postId}
    >
      <div className={cn.TimelineWrapper}>
        <div
          className={`${cn.TimelineDot} ${
            (isSlimTimeline && index === 0) ||
            (!isSlimTimeline && index < newPostsCounter)
              ? cn.TimelineDotActive
              : ''
          }`}
        />
        <div className={cn.TimelineLine} />
      </div>
      <div className={cn.TimelineContentWrapper}>
        <div className={cn.TimelineMain}>{children}</div>
        {/* ON MOBILE we inject and init a new adv */}
        {shouldInjectAdvOnMobile && (
          <div className="s-adv-shifting-container">
            <div className={`${cn.TimelineAdv} s-adv-shifting-content`}>
              <div className={cn.TimelineAdvLabel}>{t('adv.label')}</div>
              {showAdv && <Adv $advDataNode={$advTopDataNode} />}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TimelineSection;
